import React, { useState } from 'react';
import { FilterState, ProductCategory, ProductBrand } from '../types/product';
import { ChevronDown, ChevronUp } from 'lucide-react';
interface FilterSidebarProps {
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  isOpen?: boolean;
}
export function FilterSidebar({
  filters,
  setFilters,
  isOpen = true
}: FilterSidebarProps) {
  const [expandedSections, setExpandedSections] = useState({
    categories: true,
    brands: true,
    price: true
  });
  const categories: ProductCategory[] = ['Herramientas Eléctricas', 'Cerrajería', 'Plomería', 'Pintura', 'Construcción', 'Electricidad'];
  const brands: ProductBrand[] = ['Makita', 'DeWalt', 'Stanley', 'Truper', 'Black+Decker', 'ZOE', 'Bosch'];
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const toggleCategory = (category: ProductCategory) => {
    setFilters(prev => ({
      ...prev,
      categories: prev.categories.includes(category) ? prev.categories.filter(c => c !== category) : [...prev.categories, category]
    }));
  };
  const toggleBrand = (brand: ProductBrand) => {
    setFilters(prev => ({
      ...prev,
      brands: prev.brands.includes(brand) ? prev.brands.filter(b => b !== brand) : [...prev.brands, brand]
    }));
  };
  const clearFilters = () => {
    setFilters({
      categories: [],
      brands: [],
      priceRange: [0, 5000],
      search: ''
    });
  };
  const hasActiveFilters = filters.categories.length > 0 || filters.brands.length > 0;
  return <div className={`bg-white rounded-lg border border-gray-100 overflow-hidden ${!isOpen && 'hidden md:block'}`} data-id="element-72">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 flex justify-between items-center" data-id="element-73">
        <h3 className="font-semibold text-gray-900" data-id="element-74">Filtrar por</h3>
        {hasActiveFilters && <button onClick={clearFilters} className="text-xs text-[#0056b3] hover:underline" data-id="element-75">
            Limpiar todo
          </button>}
      </div>

      {/* Categories */}
      <div className="border-b border-gray-100" data-id="element-76">
        <button onClick={() => toggleSection('categories')} className="w-full p-4 flex justify-between items-center text-left hover:bg-gray-50" data-id="element-77">
          <span className="font-medium text-gray-900 text-sm" data-id="element-78">Categorías</span>
          {expandedSections.categories ? <ChevronUp className="w-4 h-4 text-gray-400" data-id="element-79" /> : <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-80" />}
        </button>
        {expandedSections.categories && <div className="px-4 pb-4 space-y-2" data-id="element-81">
            {categories.map(category => <label key={category} className="flex items-center gap-3 cursor-pointer group" data-id="element-82">
                <input type="checkbox" checked={filters.categories.includes(category)} onChange={() => toggleCategory(category)} className="w-4 h-4 rounded border-gray-300 text-[#0056b3] focus:ring-[#0056b3]" data-id="element-83" />
                <span className={`text-sm ${filters.categories.includes(category) ? 'text-gray-900 font-medium' : 'text-gray-600'}`} data-id="element-84">
                  {category}
                </span>
              </label>)}
          </div>}
      </div>

      {/* Brands */}
      <div className="border-b border-gray-100" data-id="element-85">
        <button onClick={() => toggleSection('brands')} className="w-full p-4 flex justify-between items-center text-left hover:bg-gray-50" data-id="element-86">
          <span className="font-medium text-gray-900 text-sm" data-id="element-87">Marcas</span>
          {expandedSections.brands ? <ChevronUp className="w-4 h-4 text-gray-400" data-id="element-88" /> : <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-89" />}
        </button>
        {expandedSections.brands && <div className="px-4 pb-4 space-y-2" data-id="element-90">
            {brands.map(brand => <label key={brand} className="flex items-center gap-3 cursor-pointer group" data-id="element-91">
                <input type="checkbox" checked={filters.brands.includes(brand)} onChange={() => toggleBrand(brand)} className="w-4 h-4 rounded border-gray-300 text-[#0056b3] focus:ring-[#0056b3]" data-id="element-92" />
                <span className={`text-sm ${filters.brands.includes(brand) ? 'text-gray-900 font-medium' : 'text-gray-600'}`} data-id="element-93">
                  {brand}
                </span>
              </label>)}
          </div>}
      </div>

      {/* Price Range */}
      <div data-id="element-94">
        <button onClick={() => toggleSection('price')} className="w-full p-4 flex justify-between items-center text-left hover:bg-gray-50" data-id="element-95">
          <span className="font-medium text-gray-900 text-sm" data-id="element-96">Precio</span>
          {expandedSections.price ? <ChevronUp className="w-4 h-4 text-gray-400" data-id="element-97" /> : <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-98" />}
        </button>
        {expandedSections.price && <div className="px-4 pb-4" data-id="element-99">
            <div className="flex items-center gap-2" data-id="element-100">
              <div className="flex-1" data-id="element-101">
                <label className="text-xs text-gray-500 mb-1 block" data-id="element-102">
                  Mínimo
                </label>
                <input type="number" placeholder="$0" className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0056b3]" value={filters.priceRange[0] || ''} onChange={e => setFilters(prev => ({
              ...prev,
              priceRange: [Number(e.target.value), prev.priceRange[1]]
            }))} data-id="element-103" />
              </div>
              <span className="text-gray-300 mt-5" data-id="element-104">—</span>
              <div className="flex-1" data-id="element-105">
                <label className="text-xs text-gray-500 mb-1 block" data-id="element-106">
                  Máximo
                </label>
                <input type="number" placeholder="$5000" className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-[#0056b3]" value={filters.priceRange[1] || ''} onChange={e => setFilters(prev => ({
              ...prev,
              priceRange: [prev.priceRange[0], Number(e.target.value)]
            }))} data-id="element-107" />
              </div>
            </div>
          </div>}
      </div>
    </div>;
}